/**
 * buffetActions.js — Firestore writes for the Sunday dosa buffet board.
 *
 * Collection: buffetOrders. One doc per customer order, scoped to a day via
 * `dateKey`. Per-unit "made" counts are updated transactionally so multiple
 * devices (counter + griddle) never clobber each other.
 */
import {
  collection, addDoc, doc, runTransaction, serverTimestamp, deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { todayKey } from './buffetUtils';

let _seq = 0;
function itemId() {
  _seq += 1;
  return `d${Date.now().toString(36)}_${_seq}_${Math.random().toString(36).slice(2, 7)}`;
}

function withIds(items) {
  return (items || []).map(it => ({
    id: it.id || itemId(),
    flavor: it.flavor || 'plain',
    ghee: !!it.ghee,
    onion: !!it.onion,
    name: it.name,
    qty: Math.max(1, Math.floor(it.qty) || 1),
    made: Math.max(0, Math.floor(it.made) || 0),
  }));
}

/** Create a new buffet order from a draft list of dosa lines. */
export async function createBuffetOrder(customerName, items, nameImage = null) {
  const clean = withIds(items).filter(it => it.qty > 0);
  if (clean.length === 0) return null;
  return addDoc(collection(db, 'buffetOrders'), {
    customerName: (customerName || '').trim() || 'Guest',
    nameImage: nameImage || null,   // handwritten name (PNG data URL), optional
    items: clean,
    dateKey: todayKey(),
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),     // client sort key (serverTimestamp is null until synced)
    completedAt: null,
    updatedAt: serverTimestamp(),
  });
}

/** Mark an entire order done (all dosas made) — moves it to Completed. */
export async function completeBuffetOrder(orderId) {
  const ref = doc(db, 'buffetOrders', orderId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const items = (data.items || []).map(it => ({ ...it, made: it.qty || 0 }));
    tx.update(ref, { items, completedAt: serverTimestamp(), updatedAt: serverTimestamp() });
  });
}

/** Send a completed order back to Active. */
export async function reopenBuffetOrder(orderId) {
  const ref = doc(db, 'buffetOrders', orderId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const items = (data.items || []).map(it => ({ ...it, made: 0 }));
    tx.update(ref, { items, completedAt: null, updatedAt: serverTimestamp() });
  });
}

/** Adjust a line's made-count by delta (+1 when a dosa is cooked, -1 to undo). */
export async function bumpDosaMade(orderId, lineId, delta) {
  const ref = doc(db, 'buffetOrders', orderId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const items = (data.items || []).map(it => {
      if (it.id !== lineId) return it;
      const made = Math.max(0, Math.min(it.qty || 0, (it.made || 0) + delta));
      return { ...it, made };
    });
    tx.update(ref, {
      items,
      updatedAt: serverTimestamp(),
    });
  });
}

/** Remove an order (mistake / walk-off). */
export async function removeBuffetOrder(orderId) {
  await deleteDoc(doc(db, 'buffetOrders', orderId));
}
